import React, { useState, useEffect } from 'react';
import { BookCreate, Book } from '../types';
import { apiService } from '../services';

interface BookFormProps {
  onSubmit: (data: BookCreate, coverFile?: File) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
  initialData?: Book;
}

const emptyForm: BookCreate = {
  edition_num: '',
  title: '',
  author: '',
  description: '',
  price: null,
  currency: 'EUR',
  external_link: '',
  year: undefined,
  is_published: true,
};

const toForm = (data?: Book): BookCreate => ({
  edition_num: data?.edition_num || '',
  title: data?.title || '',
  author: data?.author || '',
  description: data?.description || '',
  price: data?.price ?? null,
  currency: data?.currency || 'EUR',
  external_link: data?.external_link || '',
  year: data?.year,
  is_published: data?.is_published ?? true,
});

export const BookForm: React.FC<BookFormProps> = ({
  onSubmit,
  onCancel,
  loading,
  error,
  initialData,
}) => {
  const [formData, setFormData] = useState<BookCreate>(toForm(initialData));
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const isEditing = !!initialData?.id;

  useEffect(() => {
    if (initialData) {
      setFormData(toForm(initialData));
      setCoverFile(null);
    } else {
      setFormData(emptyForm);
      setCoverFile(null);
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title) return;
    // Normalise : chaînes vides -> null pour les champs optionnels
    const payload: BookCreate = {
      ...formData,
      edition_num: formData.edition_num?.trim() || null,
      author: formData.author?.trim() || null,
      external_link: formData.external_link?.trim() || null,
    };
    await onSubmit(payload, coverFile || undefined);
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCoverFile(e.target.files[0]);
    }
  };

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
      <div className='flex justify-between items-center mb-6'>
        <h2 className='text-lg font-semibold text-gray-900'>
          {isEditing ? "Modifier l'édition" : 'Créer une nouvelle édition'}
        </h2>
      </div>

      {error && (
        <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md'>
          <p className='text-red-600 text-sm'>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div className='grid grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              N° d'édition
            </label>
            <input
              type='text'
              value={formData.edition_num || ''}
              onChange={(e) =>
                setFormData({ ...formData, edition_num: e.target.value })
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='ex. 187-188'
            />
          </div>
          <div className='col-span-2'>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Titre *
            </label>
            <input
              type='text'
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='Titre français / Titl brezhoneg'
              required
            />
          </div>
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Auteur
          </label>
          <input
            type='text'
            value={formData.author || ''}
            onChange={(e) =>
              setFormData({ ...formData, author: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            placeholder="Auteur de l'édition"
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Description
          </label>
          <textarea
            value={formData.description || ''}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            rows={3}
            placeholder="Description de l'édition"
          />
        </div>

        <div className='grid grid-cols-3 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Prix
            </label>
            <input
              type='number'
              step='0.01'
              min='0'
              value={formData.price ?? ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  price: e.target.value ? parseFloat(e.target.value) : null,
                })
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='ex. 10'
            />
            <p className='mt-1 text-xs text-gray-400'>
              Laisser vide = non proposé à la vente
            </p>
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Devise
            </label>
            <select
              value={formData.currency || 'EUR'}
              onChange={(e) =>
                setFormData({ ...formData, currency: e.target.value })
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value='EUR'>EUR (€)</option>
              <option value='USD'>USD ($)</option>
              <option value='GBP'>GBP (£)</option>
            </select>
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Année
            </label>
            <input
              type='number'
              value={formData.year || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  year: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='Année'
            />
          </div>
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Lien externe
          </label>
          <input
            type='url'
            value={formData.external_link || ''}
            onChange={(e) =>
              setFormData({ ...formData, external_link: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            placeholder='https://…'
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Couverture (optionnel)
          </label>
          {initialData?.cover_image && (
            <div className='mb-3'>
              <p className='text-sm text-gray-600 mb-2'>Couverture actuelle :</p>
              <img
                src={apiService.getBookCoverUrl(initialData.id)}
                alt={initialData.title}
                className='max-h-40 w-auto rounded-lg border border-gray-200'
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
          <input
            type='file'
            accept='image/jpeg,image/png,image/gif,image/webp'
            onChange={handleCoverChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          {coverFile && (
            <p className='mt-1 text-sm text-gray-500'>
              Nouveau fichier sélectionné : {coverFile.name}
            </p>
          )}
        </div>

        <div>
          <label className='flex items-center gap-2'>
            <input
              type='checkbox'
              checked={formData.is_published}
              onChange={(e) =>
                setFormData({ ...formData, is_published: e.target.checked })
              }
              className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
            />
            <span className='text-sm font-medium text-gray-700'>Publier</span>
          </label>
        </div>

        <div className='flex gap-3 pt-4'>
          <button
            type='submit'
            disabled={loading || !formData.title}
            className='flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {loading
              ? isEditing
                ? 'Mise à jour...'
                : 'Création...'
              : isEditing
              ? 'Mettre à jour'
              : "Créer l'édition"}
          </button>
          <button
            type='button'
            onClick={onCancel}
            className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50'
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
